import React, {useState} from "react";
import {Carousel} from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import {ImageCarouselSlideButton} from "../imageCarouselSlideButton/ImageCarouselSlideButton";
import {SliderButtonPosition} from "../vehicleSliderButton/VehicleSliderButton";
import {ImageCounter} from "../imageCounter/ImageCounter";
import {RENDER_DISTANCE} from "../vehicleSlider/VehicleSlider";
import styles from "./ImageCarousel.module.css";

interface ImageCarouselProps {
    brandColour: string;
    images: string[];
    onImageClick?: (e?: any) => void;
    height?: number;
    objectFit?: 'contain' | 'cover';
    className?: string;
    isInteractive?: boolean;
    selectedIndex?: number;
    onSelectedIndexChange?: (index: number) => void;
}

export const ImageCarousel: React.FC<ImageCarouselProps> = ({
                                                                brandColour,
                                                                images,
                                                                onImageClick,
                                                                height = 220,
                                                                className = '',
                                                                isInteractive = true,
                                                                selectedIndex,
                                                                onSelectedIndexChange
                                                            }) => {
    const currentImageIndex = selectedIndex ?? 0;
    const setIndex = (next: number) => {
        onSelectedIndexChange?.(next);
    };
    const isMobile = false;
    const len = images.length;
    const idx = currentImageIndex;

    let start = 0;
    if (len > 5) {
        start = Math.min(Math.max(idx - 2, 0), len - 5);
    }
    const end = Math.min(start + 4, len - 1);
    const inWindow = (i: number) => i >= start && i <= end;

    const dotCSS = (i: number, isSelected: boolean) => {
        const isLeftEdge = i === start && start > 0;
        const isRightEdge = i === end && end < len - 1;
        const size = isSelected ? 6 : isLeftEdge || isRightEdge ? 3 : 5;
        return {
            width: size,
            height: size,
            backgroundColor: isSelected ? "#FFC756" : "transparent",
            borderRadius: "100%"
        };
    };

    const [hover, setHover] = useState(false);

    return (
        <div
            className={`${styles.root} ${className}`}
            onTouchStart={isInteractive ? (e) => e.stopPropagation() : undefined}
            onPointerDown={isInteractive ? (e) => e.stopPropagation() : undefined}
            onMouseDown={isInteractive ? (e) => e.stopPropagation() : undefined}
            onMouseEnter={isInteractive ? () => setHover(true) : undefined}
            onMouseLeave={isInteractive ? () => setHover(false) : undefined}
            style={isInteractive ? {} : {pointerEvents: "none", touchAction: "pan-x"}}
        >
            {!isMobile && isInteractive && (
                <>
                    <div className={styles.arrowLeft}>
                        <ImageCarouselSlideButton
                            brandColour={brandColour}
                            onClick={() => setIndex(currentImageIndex - 1)}
                            position={SliderButtonPosition.BUTTON_LEFT}
                            isDisabled={currentImageIndex === 0 || !hover}
                        />
                    </div>
                    <div className={styles.arrowRight}>
                        <ImageCarouselSlideButton
                            brandColour={brandColour}
                            onClick={() => setIndex(currentImageIndex + 1)}
                            position={SliderButtonPosition.BUTTON_RIGHT}
                            isDisabled={currentImageIndex === images.length - 1 || !hover}
                        />
                    </div>
                </>
            )}

            <div className={styles.counter}>
                <ImageCounter
                    brandColour={brandColour}
                    currentIndex={currentImageIndex}
                    images={images}
                />
            </div>

            <div className={isInteractive ? "" : styles.nonInteractive}>
                <Carousel
                    selectedItem={currentImageIndex}
                    onChange={isInteractive ? setIndex : () => {
                    }}
                    emulateTouch={isInteractive}
                    swipeScrollTolerance={isInteractive ? 1 : 999}
                    showArrows={false}
                    showThumbs={false}
                    showStatus={false}
                    showIndicators={isMobile && isInteractive}
                    renderIndicator={(onClick: any, isSelected: any, i: any, label: any) =>
                        !inWindow(i) ? null : (
                            <li
                                key={i}
                                onClick={isInteractive ? onClick : undefined}
                                onKeyDown={isInteractive ? onClick : undefined}
                                role="button"
                                tabIndex={isInteractive ? 0 : -1}
                                aria-label={`${label} ${i + 1}`}
                                className={`${styles.indicator} ${
                                    isInteractive ? styles.indicatorInteractive : styles.indicatorNonInteractive
                                }`}
                                style={dotCSS(i, isSelected)}
                            >
                                {!isSelected && (
                                    <svg width="100%" height="100%" viewBox="0 0 4 4" fill="none">
                                        <circle cx="2" cy="2" r="1.5" fill="#DDDDDD"/>
                                    </svg>
                                )}
                            </li>
                        )
                    }
                    centerMode={false}
                    dynamicHeight={false}
                    className={styles.carouselWrapper}
                >
                    {images.map((src, i) => {
                        const distance = Math.abs(currentImageIndex - i);
                        const shouldRender = distance <= RENDER_DISTANCE;

                        return (
                            <div
                                key={i}
                                className={styles.slide}
                                style={{height: `${height}px`}}
                                onClick={isInteractive ? onImageClick : undefined}
                            >
                                {shouldRender ? (
                                    <div className={styles.slideContent}>
                                        <img
                                            src={src || "/no-image.jpeg"}
                                            alt={`Slide ${i + 1}`}
                                            className={styles.image}
                                            draggable={false}
                                            onError={(e) => {
                                                const img = e.currentTarget;
                                                if (img.dataset.fallbackApplied === "true") return;

                                                img.dataset.fallbackApplied = "true";
                                                img.src = "/no-image.jpeg";
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className={styles.fallback}/>
                                )}
                            </div>
                        );
                    })}
                </Carousel>
            </div>
        </div>
    );
};
